import { Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { Switch } from '@/components/form/baseInputs/Switch';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';

import {
  useUpdateIntegrationConfigurationFromAdminMutation,
  useUpdateTenantSecurityConfigurationFromAdminMutation,
} from '../graphql/AdminConfigurationsPage.generated';

interface ToggleSettingsSectionProps {
  integrationConfigurationId: string | undefined;
  enableLlmByok: boolean;
  tenantSecurityConfigurationId: string | undefined;
  luminaryAccessControlEnabled: boolean;
}

export function ToggleSettingsSection({
  integrationConfigurationId,
  enableLlmByok,
  tenantSecurityConfigurationId,
  luminaryAccessControlEnabled,
}: ToggleSettingsSectionProps) {
  const { showFeedback, createErrorFeedback } = useFeedback();

  const [updateIntegrationConfiguration, { loading: byompLoading }] =
    useUpdateIntegrationConfigurationFromAdminMutation({
      onCompleted: () => {
        showFeedback('Azure BYOMP configuration updated', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback(
        'Failed to update Azure BYOMP configuration'
      ),
    });

  const [updateTenantSecurityConfiguration, { loading: securityLoading }] =
    useUpdateTenantSecurityConfigurationFromAdminMutation({
      onCompleted: () => {
        showFeedback('Tenant security access updated', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback('Failed to update tenant security access'),
    });

  const handleByompToggle = useCallback(
    (checked: boolean) => {
      if (!integrationConfigurationId) return;

      void updateIntegrationConfiguration({
        variables: {
          input: {
            id: integrationConfigurationId,
            update: {
              enableLlmByok: checked,
            },
          },
        },
      });
    },
    [integrationConfigurationId, updateIntegrationConfiguration]
  );

  const handleSecurityToggle = useCallback(
    (checked: boolean) => {
      if (!tenantSecurityConfigurationId) return;

      void updateTenantSecurityConfiguration({
        variables: {
          input: {
            id: tenantSecurityConfigurationId,
            update: {
              luminaryAccessControlEnabled: checked,
            },
          },
        },
      });
    },
    [tenantSecurityConfigurationId, updateTenantSecurityConfiguration]
  );

  return (
    <Stack spacing={2}>
      {integrationConfigurationId ? (
        <Switch
          label="Azure BYOMP"
          value={enableLlmByok}
          disabled={byompLoading}
          onChange={(e) => handleByompToggle(e.target.checked)}
        />
      ) : (
        <Typography variant="body-xs">
          No integration configuration found.
        </Typography>
      )}
      {tenantSecurityConfigurationId ? (
        <Switch
          label="Luminary access control"
          value={luminaryAccessControlEnabled}
          disabled={securityLoading}
          onChange={(e) => handleSecurityToggle(e.target.checked)}
        />
      ) : (
        <Typography variant="body-xs">
          No security configuration found.
        </Typography>
      )}
    </Stack>
  );
}
